import { backToNative, setNavigationBar, togglePullToRefresh } from '@docs/common'
import { useAsyncEffect, useScroll } from 'ahooks'
import { Tabs, Swiper, SwiperRef } from 'antd-mobile'
import { getVariantsBySKUList } from 'common/request'
import { trackDetailSeeAll, trackNavigationModuleClick, trackNavigationTabClick } from 'common/track'
import Banner from 'components/Banner'
import Community from 'components/Community'
import Feature from 'components/Feature'
import Sku from 'components/Sku'
import Video from 'components/Video'
import NativeVideo from 'components/NativeVideo'
import { isYouTubeVideo } from '../../utils/videoUtils'
import { find } from 'lodash'
import { parse } from 'qs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Element, Events, scroller as ReactScroller } from 'react-scroll'
import getGuide, {
  IGuide,
  IGuideBanner,
  IGuideCommunity,
  IGuideComponentType,
  IGuideFeature,
  IGuideSku,
  IGuideVideo,
} from 'servers/getGuide'
import { trackGuidePageView } from '../../common/track'
import styles from './index.module.scss'
import classes from 'classnames'
import { getSearchCountry, resolvePnFromSn, triggerLink } from 'common/util'
import useAraLayout from 'common/useAraLayout'

const TABSTYLE = {
  '--active-line-height': '3px',
  '--active-line-color': '#00618A',
  '--fixed-active-line-width': '36px',
  '--active-title-color': '#000',
  '--title-font-size': '14px',
}

const NAV_HEIGHT = 60
const TAB_HEIGHT = 40
const GAP = 25

const NavigationPage = () => {
  const [guide, setGuide] = useState<IGuide | null>(null)
  const [activeKey, setActiveKey] = useState<string>('0')
  const [products, setProducts] = useState<IProduct[]>()
  const tabRef = useRef<HTMLDivElement>(null)
  const scrollContainer = useRef<HTMLDivElement>(null)
  const scroller = useScroll()
  const isScrollingByChangeTab = useRef(false)
  const swiperBannerRef = useRef<SwiperRef>(null)
  const swiperVideoRef = useRef<SwiperRef>(null)
  const [activeVideoIndex, setActiveVideoIndex] = useState<number>(0)
  useAraLayout()

  const {
    id: searchId,
    pn,
    customPn,
    tabIndex,
    hideNavBar,
  } = parse(window.location.search.substr(1)) as unknown as {
    id: string
    pn: string
    customPn: string
    tabIndex: string
    hideNavBar?: boolean
  }
  // 如果链接有自定义customPn参数，使用customPn，优先级大于参数pn
  // 链接参数 pn 来源值是app传过来的device_sn，也可以是cms上面生成的link带的参数
  const newPn = customPn && customPn !== 'null' && customPn !== 'undefined' && customPn.trim() !== '' ? customPn : resolvePnFromSn(pn);

  // id如果链接没有 取guide下面的id
  const id = useMemo(() => {
    // start_ai_generated
    if (searchId) return searchId
    // end_ai_generated
    return guide?.id || ''
    // start_ai_generated
  }, [guide?.id, searchId])
  // end_ai_generated

  const { title = '', tabs = [], banner = [] } = guide || {}

  const skus = useMemo(() => {
    const one = find(tabs, o => o.__component === IGuideComponentType.Sku) as IGuideSku
    return one?.listing || []
  }, [tabs])

  const store = useMemo(() => {
    // start_ai_generated
    const one = find(tabs, o => o.__component === IGuideComponentType.Sku) as IGuideSku
    // end_ai_generated
    return one?.store?.split('_')[1] || ''
  }, [tabs])

  // 滚动到 tab 位置时，固定 tab
  const isFixed = useMemo(() => {
    const top = (tabRef.current?.offsetTop || 0) - 60
    if (!scroller?.top) return false
    return scroller.top >= top
  }, [scroller?.top])

  // 切换 tab 时滚动到对应模块
  const handleTabChange = useCallback(
    (key: string) => {
      const tab = tabs[+key]
      const locale = guide?.locale || ''
      trackNavigationTabClick(id, tab.title || tab.__component || '', newPn, locale)
      setActiveKey(key)
      const element = document.getElementById(key)
      if (element) {
        const offset = -(NAV_HEIGHT + TAB_HEIGHT + GAP - 1)
        const elementPosition = element.offsetTop
        const viewportHeight = window.innerHeight
        const documentHeight = document.documentElement.scrollHeight
        const maxScrollableDistance = documentHeight - viewportHeight
        const calculatedPosition = Math.min(elementPosition - offset, maxScrollableDistance)

        // 检查内容长度
        const isContentShort = documentHeight - viewportHeight <= 140

        if (isContentShort) {
          // 内容较短时，直接滚动到目标位置
          window.scrollTo({
            top: calculatedPosition,
            behavior: 'smooth',
          })
        } else {
          // 其他情况使用 ReactScroller.scrollTo
          ReactScroller.scrollTo(key, {
            duration: 500,
            smooth: true,
            offset: offset,
          })
        }
      }
    },
    [guide?.locale, id, newPn, tabs]
  )

  useAsyncEffect(async () => {
    const guide = await getGuide({ id, pn: newPn })
    setGuide(guide)

    // 注册 tab 切换时的滚动事件
    Events.scrollEvent.register('begin', () => {
      isScrollingByChangeTab.current = true
    })
    Events.scrollEvent.register('end', () => {
      isScrollingByChangeTab.current = false
    })
  }, [])

  useAsyncEffect(async () => {
    if (!skus.length) return
    const res = await getVariantsBySKUList(skus, store)
    const data = skus.map(sku => find(res, o => o.sku === sku)).filter(item => item) as IProduct[]
    setProducts(data)
  }, [skus])

  useEffect(() => {
    if (!scroller) return
    if (isScrollingByChangeTab.current) return
    let currentKey = activeKey
    for (let i = 0; i < tabs.length; i++) {
      const key = `${i}`
      const element = document.getElementById(key)
      if (!element) continue
      const rect = element.getBoundingClientRect()
      if (rect.top <= NAV_HEIGHT + TAB_HEIGHT + GAP) {
        currentKey = key
      } else {
        break
      }
    }
    setActiveKey(currentKey)
  }, [scroller, activeKey, tabs, tabIndex])

  // 根据 tabIndex 滚动到对应模块，并设置 activeKey
  useEffect(() => {
    // 解析 tabIndex 参数
    const index = parseInt(tabIndex, 10) - 1
    // 检查 tabIndex 是否有效
    if (!isNaN(index) && index >= 0 && index < tabs.length) {
      // 设置 activeKey
      if (tabIndex && tabs.length) {
        setTimeout(() => {
          ReactScroller.scrollTo(String(index), {
            duration: 500,
            smooth: true,
            offset: -(NAV_HEIGHT + TAB_HEIGHT + GAP - 1),
          })
          setActiveKey(String(index))
        }, 800)
      }
    }
    window.scrollTo(0, 0)
  }, [tabIndex, tabs.length])

  useEffect(() => {
    // 设置导航
    // 根据 路径上hideNavBar 参数决定是否隐藏h5导航条
    if (!hideNavBar) {
      setNavigationBar({
        title: title || '',
        onBack: () => {
          if ((window as any).__IS_APP__) {
            backToNative()
          } else {
            if (window.history.length > 1) {
              window.history.back()
            }
          }
        },
      })
    }
  }, [handleTabChange, hideNavBar, tabIndex, tabs.length, title])

  useEffect(() => {
    if (scroller?.top && scroller.top > 0) {
      togglePullToRefresh(false)
    } else {
      togglePullToRefresh(true)
    }
    return () => {
      togglePullToRefresh(true)
    }
  }, [scroller?.top])

  // 页面访问埋点
  useEffect(() => {
    const locale = guide?.locale || ''
    id && trackGuidePageView(id, newPn, locale)
  }, [guide?.locale, id, newPn])

  const handleBannerIndicatorClick = (index: number) => {
    swiperBannerRef.current?.swipeTo(index)
  }

  const handleVideoIndicatorClick = (index: number) => {
    swiperVideoRef.current?.swipeTo(index)
  }
  // 在 Swiper 组件的 onIndexChange 事件中停止当前视频播放
  const handleVideoChange = (index: number) => {
    setActiveVideoIndex(index)
  }

  // see all 跳转
  const openLink = (tab: any) => {
    if (!tab?.link) return
    const locale = guide?.locale || ''
    id && trackDetailSeeAll(id, tab.title || tab.__component || '', newPn, locale)
    const country = getSearchCountry()
    const link = tab?.link.includes('?') ? `${tab?.link}&country=${country}` : `${tab?.link}?country=${country}`
    triggerLink(link)
  }

  return (
    <div className={`${styles.container} ${hideNavBar ? styles.hideNavBar : ''}`} ref={scrollContainer} id="container">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <section className={styles.banner}>{banner?.[0]?.url && <img src={banner?.[0]?.url} alt="banner" />}</section>

      <section className={styles.tabs} ref={tabRef}>
      <div className={`${isFixed ? `${styles.fixed} ${hideNavBar ? styles.hideNavBars : ''}` : ''}`}>
          <Tabs activeLineMode="fixed" style={TABSTYLE} onChange={handleTabChange} activeKey={activeKey}>
            {tabs.map((tab, i) => (
              <Tabs.Tab title={tab.title} key={`${i}`}></Tabs.Tab>
            ))}
          </Tabs>
        </div>
      </section>

      {tabs.map((tab, i) => (
        <Element name={`${i}`} id={`${i}`} key={`${i}`} className={styles.block}>
          <div className={styles.rowTitle}>
            <div className={styles.title}>{tab.title}</div>
            {tab?.isShowSeeAll && (
              <div className={styles.more} onClick={() => openLink(tab)}>
                {tab?.moreTitle} <i className={styles.arrow}></i>
              </div>
            )}
          </div>

          <div className={styles.contents}>
            <div className={styles.content}>
              {tab.__component === IGuideComponentType.Feature &&
                (tab as IGuideFeature).listing?.map(feature => (
                  <Feature
                    key={feature.id}
                    {...feature}
                    art_id={id}
                    layout={(tab as IGuideFeature)?.layout}
                    befortrigger={() => {
                      trackNavigationModuleClick(feature.title || '', {
                        id,
                        module: tab.__component,
                        value: feature.title || '',
                        language: guide?.locale || '',
                        pn: newPn,
                        sub_module: tab.title,
                      })
                    }}
                  />
                ))}
            </div>
            <div
              className={
                (tab as IGuideBanner)?.layout === 'horizontal'
                  ? classes(styles.content, styles.contentBanner)
                  : classes(styles.content)
              }
            >
              {tab.__component === IGuideComponentType.Banner &&
                (tab as IGuideBanner)?.listing.length > 0 &&
                // 默认竖向布局 vertical
                // 根据cms配置的layout来判断 横向布局 horizontal
                ((tab as IGuideBanner)?.layout === 'horizontal' ? (
                  <Swiper
                    autoplay={false}
                    loop={false}
                    className={styles.swiperBanner}
                    slideSize={80}
                    trackOffset={20}
                    stuckAtBoundary={true}
                    defaultIndex={0}
                    ref={swiperBannerRef}
                    indicator={(total, current) => (
                      <div className={styles.customIndicator}>
                        {Array(total)
                          .fill(0)
                          .map((_, index) => (
                            <div
                              key={index}
                              className={`${styles.indicatordot} ${
                                current === index ? `${styles.indicatordotActive}` : ''
                              }`}
                              onClick={() => handleBannerIndicatorClick(index)}
                            />
                          ))}
                      </div>
                    )}
                  >
                    {(tab as IGuideBanner)?.listing?.map((banner, index, array) => (
                      <Swiper.Item key={banner.id}>
                        <Banner
                          {...banner}
                          art_id={id}
                          befortrigger={() => {
                            trackNavigationModuleClick(banner.title || '', {
                              id,
                              module: tab.__component,
                              value: banner.title || '',
                              language: guide?.locale || '',
                              pn: newPn,
                              sub_module: tab.title,
                            })
                          }}
                        />
                      </Swiper.Item>
                    ))}
                  </Swiper>
                ) : (
                  (tab as IGuideBanner)?.listing?.map(banner => (
                    <Banner
                      key={banner.id}
                      {...banner}
                      art_id={id}
                      befortrigger={() => {
                        trackNavigationModuleClick(banner.title || '', {
                          id,
                          module: tab.__component,
                          value: banner.title || '',
                          language: guide?.locale || '',
                          pn: newPn,
                          sub_module: tab.title,
                        })
                      }}
                    />
                  ))
                ))}
            </div>
            <div className={styles.content}>
              {tab.__component === IGuideComponentType.Sku &&
                products?.map(pro => (
                  <Sku
                    key={pro.sku}
                    {...pro}
                    art_id={id}
                    befortrigger={() => {
                      trackNavigationModuleClick(pro.product.title || '', {
                        id,
                        module: tab.__component,
                        value: pro.sku || '',
                        language: guide?.locale || '',
                        pn: newPn,
                        sub_module: tab.title,
                      })
                    }}
                  />
                ))}
            </div>
            <div className={classes(styles.content, styles.contentBanner)}>
              {tab.__component === IGuideComponentType.Video &&
                (tab as IGuideVideo)?.listing.length > 0 &&
                ((tab as IGuideBanner)?.layout === 'horizontal' ? (
                  <Swiper
                    autoplay={false}
                    loop={false}
                    className={styles.swiperVideo}
                    slideSize={90}
                    trackOffset={5}
                    stuckAtBoundary={true}
                    defaultIndex={0}
                    ref={swiperVideoRef}
                    onIndexChange={handleVideoChange}
                    indicator={(total, current) => (
                      <div className={styles.customIndicator}>
                        {Array(total)
                          .fill(0)
                          .map((_, index) => (
                            <div
                              key={index}
                              className={`${styles.indicatordot} ${
                                current === index ? `${styles.indicatordotActive}` : ''
                              }`}
                              onClick={() => handleVideoIndicatorClick(index)}
                            />
                          ))}
                      </div>
                    )}
                  >
                    {(tab as IGuideVideo)?.listing?.map((video, index, array) => (
                      <Swiper.Item key={video.id}>
                        {isYouTubeVideo(video.value || '') ? (
                          <Video
                            key={video.id}
                            sub_module={tab.title}
                            pn={newPn}
                            language={guide?.locale || ''}
                            layout={(tab as IGuideBanner)?.layout}
                            isPlaying={index !== activeVideoIndex}
                            {...video}
                            art_id={+id}
                            module={tab.__component}
                          />
                        ) : (
                          <NativeVideo
                            key={video.id}
                            sub_module={tab.title}
                            pn={newPn}
                            language={guide?.locale || ''}
                            layout={(tab as IGuideBanner)?.layout}
                            isPlaying={index !== activeVideoIndex}
                            {...video}
                            art_id={+id}
                            module={tab.__component}
                          />
                        )}
                      </Swiper.Item>
                    ))}
                  </Swiper>
                ) : (
                  (tab as IGuideVideo)?.listing?.map(video => (
                    isYouTubeVideo(video.value || '') ? (
                      <Video
                        key={video.id}
                        {...video}
                        language={guide?.locale || ''}
                        pn={newPn}
                        sub_module={tab.title}
                        art_id={+id}
                        module={tab.__component}
                        style={{ marginBottom: '10px', padding: '0 15px' }}
                      />
                    ) : (
                      <NativeVideo
                        key={video.id}
                        {...video}
                        language={guide?.locale || ''}
                        pn={newPn}
                        sub_module={tab.title}
                        art_id={+id}
                        module={tab.__component}
                        style={{ marginBottom: '10px', padding: '0 15px' }}
                      />
                    )
                  ))
                ))}
            </div>
            <div className={styles.content}>
              {tab.__component === IGuideComponentType.Community &&
                (tab as IGuideCommunity)?.listing?.map(cid => (
                  <Community
                    key={cid}
                    id={+cid}
                    befortrigger={() => {
                      trackNavigationModuleClick(cid, {
                        id,
                        module: tab.__component,
                        value: cid,
                        language: guide?.locale || '',
                        pn: newPn,
                        sub_module: tab.title,
                      })
                    }}
                  />
                ))}
            </div>
          </div>
        </Element>
      ))}
    </div>
  )
}

export default NavigationPage
